import { type Database } from "@fitness-app/supabase";

import { type TraineeAiReportModelOptionId } from "./TraineeAiReportConfig";

// Trainee AI report — an on-demand report about one client, requested from the
// admin TraineeProfile and generated by the trainee-report agent in packages/ai.
// The audience is fixed at request time and steers the whole generation: a
// 'trainer' report analyses the client FOR the trainer (with guidance on how to
// talk to her), a 'client' report is written TO the client and becomes readable
// by her once completed (RLS arm on trainee_ai_report).

export type TraineeAiReportEntity = Database["public"]["Tables"]["trainee_ai_report"]["Row"];

export enum TraineeAiReportAudience {
  Trainer = "trainer",
  Client = "client",
}

export enum TraineeAiReportStatus {
  Queued = "queued",
  Completed = "completed",
  Failed = "failed",
}

/** Lookback windows offered in the admin UI; the API rejects anything else. */
export const traineeAiReportAllowedDays = [7, 14, 30] as const;
export type TraineeAiReportDays = (typeof traineeAiReportAllowedDays)[number];

export interface TraineeAiReportMetadata {
  model?: {
    provider: string;
    optionId: string;
    name: string;
    label: string;
    reasoningEffort?: string;
  };
  error?: string;
  /** trigger.dev run of the generation + a read-scoped public token, so the admin
   *  can follow progress live (useRealtimeRun / useRealtimeStream). */
  runId?: string;
  accessToken?: string;
}

// Live progress of a generation. The task mirrors the agent's tool calls into the
// run metadata (`progress`) and pipes the report text token by token into the
// stream below; the admin renders both while the row is still `queued`.

export const TRAINEE_AI_REPORT_TEXT_STREAM_KEY = "trainee-ai-report-text";

export type TraineeAiReportProgressStepStatus = "running" | "done";

// Type aliases (not interfaces) on purpose: the task hands this to trigger.dev's
// metadata.set, whose JSON type needs the implicit index signature only aliases get.
export type TraineeAiReportProgressStep = {
  key: string;
  label: string;
  status: TraineeAiReportProgressStepStatus;
};

export type TraineeAiReportProgress = {
  steps: TraineeAiReportProgressStep[];
};

/** Polish labels for the agent's tool ids; unknown tools fall back to the id. */
export const traineeAiReportStepLabels: Record<string, string> = {
  "get-client-profile": "Czytam profil klientki",
  "get-recent-activity": "Analizuję aktywność",
  "get-client-notes": "Przeglądam notatki zespołu",
  "get-progress": "Sprawdzam postępy i pomiary",
  "get-recent-chat-messages": "Czytam wiadomości z czatu",
  writing: "Piszę raport",
};

export const TRAINEE_AI_REPORT_WRITING_STEP = "writing";

/**
 * Mastra names a tool call after its REGISTRY KEY (`getClientProfileTool`), not
 * the tool's `id` (`get-client-profile`), and the same tool can also show up
 * without the `Tool` suffix in model output. Normalize every spelling to the
 * kebab-case id the label table is keyed by, so progress steps match and
 * tool-call / tool-result pairs land on one step.
 */
export const normalizeTraineeAiReportStepKey = (toolName: string): string =>
  toolName
    .trim()
    .replace(/Tool$/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[_\s]+/g, "-")
    .toLowerCase();

export const resolveTraineeAiReportStepLabel = (toolName: string): string => {
  const key = normalizeTraineeAiReportStepKey(toolName);
  return traineeAiReportStepLabels[key] ?? traineeAiReportStepLabels[toolName] ?? key;
};

export interface TraineeAiReport extends Omit<TraineeAiReportEntity, "audience" | "status" | "metadata"> {
  audience: TraineeAiReportAudience;
  status: TraineeAiReportStatus;
  metadata: TraineeAiReportMetadata | null;
}

/** Shape of app_config.data for the "trainee_ai_report" row. Model options use the
 *  dedicated traineeReport policy in the shared AI catalog. */
export interface TraineeAiReportConfigData {
  modelOptionId: TraineeAiReportModelOptionId;
}
